/**
 * Dashboard Component
 * Admin dashboard with statistics and quick links
 */

import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  VideogameAsset as GamesIcon,
  Inbox as SubmissionsIcon,
  People as ContributorsIcon,
  RateReview as ReviewIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { StatCard } from './StatCard';
import { STATUS_LABELS, RELEASE_CHANNEL_LABELS } from '../../utils/constants';

export const Dashboard = () => {
  const navigate = useNavigate();
  const { data: stats, isLoading, error } = useDashboardStats();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load dashboard stats: {error.message}
        </Alert>
      </Box>
    );
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatAction = (action) => {
    const actionMap = {
      'create_game': 'Created game',
      'edit_game': 'Edited game',
      'delete_game': 'Deleted game',
      'status_change': 'Changed status',
      'copy_game': 'Copied game',
      'cancel_modifications': 'Cancelled modifications',
      'UPDATE_CONTRIBUTOR': 'Updated contributor',
      'APPROVE_PUBLIC_LISTING': 'Approved public listing',
      'REJECT_PUBLIC_LISTING': 'Rejected public listing'
    };
    return actionMap[action] || action;
  };

  // Calculate totals for action items
  const actionItems = (stats?.submissions?.pending || 0) +
    (stats?.submissions?.responseReceived || 0) +
    (stats?.contributors?.pendingApproval || 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {/* Action Items Alert */}
      {actionItems > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have <strong>{actionItems}</strong> items requiring attention:
          {stats?.submissions?.pending > 0 && ` ${stats.submissions.pending} pending submissions`}
          {stats?.submissions?.responseReceived > 0 && `, ${stats.submissions.responseReceived} responses received`}
          {stats?.contributors?.pendingApproval > 0 && `, ${stats.contributors.pendingApproval} contributors awaiting approval`}
        </Alert>
      )}

      {/* Main Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Games"
            value={stats?.games?.total || 0}
            subtitle="In staging library"
            icon={<GamesIcon />}
            color="primary"
            onClick={() => navigate('/games')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Submissions"
            value={stats?.submissions?.pending || 0}
            subtitle={`${stats?.submissions?.thisWeek || 0} new this week`}
            icon={<SubmissionsIcon />}
            color="warning"
            onClick={() => navigate('/submissions')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="In Review"
            value={stats?.submissions?.inReview || 0}
            subtitle="Being processed"
            icon={<ReviewIcon />}
            color="info"
            onClick={() => navigate('/submissions')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Contributors"
            value={stats?.contributors?.activated || 0}
            subtitle={stats?.contributors?.pendingApproval > 0
              ? `${stats.contributors.pendingApproval} pending approval`
              : 'Active contributors'}
            icon={<ContributorsIcon />}
            color="success"
            onClick={() => navigate('/contributors')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Games by Status */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <GamesIcon color="primary" /> Games by Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              {stats?.games?.byStatus && Object.entries(stats.games.byStatus).map(([status, count]) => (
                <ListItem
                  key={status}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/games?status=${status}`)}
                >
                  <ListItemText
                    primary={STATUS_LABELS[status] || status}
                  />
                  <Chip label={count} size="small" />
                </ListItem>
              ))}
              {(!stats?.games?.byStatus || Object.keys(stats.games.byStatus).length === 0) && (
                <ListItem>
                  <ListItemText primary="No games yet" secondary="Create your first game" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Games by Channel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingIcon color="secondary" /> Production by Channel
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              {stats?.games?.byChannel && Object.entries(stats.games.byChannel).map(([channel, count]) => (
                <ListItem
                  key={channel}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/games?releaseChannel=${channel}`)}
                >
                  <ListItemText
                    primary={RELEASE_CHANNEL_LABELS[channel] || channel}
                  />
                  <Chip
                    label={count}
                    size="small"
                    color={channel === 'curated' ? 'primary' : channel === 'beta' ? 'warning' : 'default'}
                  />
                </ListItem>
              ))}
              {(!stats?.games?.byChannel || Object.keys(stats.games.byChannel).length === 0) && (
                <ListItem>
                  <ListItemText primary="No games in production" secondary="Promote games to production" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="action" /> Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
              {stats?.recentActivity?.map((activity) => (
                <ListItem key={activity.id}>
                  <ListItemText
                    primary={formatAction(activity.action)}
                    secondary={
                      <>
                        {activity.details?.gameCode || activity.details?.gameName || activity.details?.handle || `ID: ${activity.entityId}`}
                        {' - '}
                        {activity.user}
                        {' - '}
                        {formatTimestamp(activity.timestamp)}
                      </>
                    }
                  />
                </ListItem>
              ))}
              {(!stats?.recentActivity || stats.recentActivity.length === 0) && (
                <ListItem>
                  <ListItemText primary="No recent activity" secondary="Actions will appear here" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Quick Actions</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item>
            <Chip
              icon={<GamesIcon />}
              label="Create New Game"
              onClick={() => navigate('/games/new')}
              clickable
              color="primary"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Chip
              icon={<SubmissionsIcon />}
              label="Review Submissions"
              onClick={() => navigate('/submissions')}
              clickable
              color="warning"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Chip
              icon={<ContributorsIcon />}
              label="Manage Contributors"
              onClick={() => navigate('/contributors')}
              clickable
              color="success"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
